import { useState } from 'react';

import { AppIcon, GenericTable } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import {
  ApplicationPlan,
  CollegeTier,
  USER_COLLEGE_STATUSES,
  UserCollege,
  UserCollegeStatus,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, IconButton, SxProps, TableCell, TableRow, Theme, Typography } from '@mui/material';

import { AwardLetterButton } from './AwardLetterButton';
import { NetCostText } from './NetCostText';
import { PlanPicker } from './PlanPicker';
import { RequirementTags } from './RequirementTags';
import { StatusChip } from './StatusChip';
import { TierChip } from './TierChip';
import { deadlineText, groupByTier } from '../myColleges.util';

const STRINGS = appStrings.modules.myColleges;

type SortValue = string | number | null;

const SORT_VALUES = {
  college: (college: UserCollege) => college.name.toLowerCase(),
  status: (college: UserCollege) => USER_COLLEGE_STATUSES.indexOf(college.status),
  plan: (college: UserCollege) =>
    college.application_plan && STRINGS.planLabels[college.application_plan],
  deadline: (college: UserCollege) => college.my_deadline,
  netCost: (college: UserCollege) => college.net_cost,
  requirements: (college: UserCollege) =>
    Number(!college.test_blind && college.test_optional === false) +
    Number(college.essay_requirement === 'required') +
    Number(college.recommendation_requirement === 'required'),
} satisfies Record<string, (college: UserCollege) => SortValue>;

type SortField = keyof typeof SORT_VALUES;
type Sort = Readonly<{ field: SortField; direction: 'asc' | 'desc' }> | null;

const COLUMNS: readonly Readonly<{ field: SortField; width?: string }>[] = [
  { field: 'college' },
  { field: 'status', width: '120px' },
  { field: 'plan', width: '116px' },
  { field: 'deadline', width: '120px' },
  { field: 'netCost', width: '136px' },
  { field: 'requirements', width: '140px' },
];

const compareSortValues = (a: SortValue, b: SortValue) => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  return a < b ? -1 : 1;
};

const sortColleges = (colleges: readonly UserCollege[], sort: Sort) => {
  if (!sort) return colleges;

  const valueOf = SORT_VALUES[sort.field];
  const sign = sort.direction === 'asc' ? 1 : -1;

  return [...colleges].sort((a, b) => {
    const [first, second] = [valueOf(a), valueOf(b)];

    if (first === null || second === null) return compareSortValues(first, second);

    return sign * compareSortValues(first, second);
  });
};

const nextSort = (sort: Sort, field: SortField): Sort => {
  if (sort?.field !== field) return { field, direction: 'asc' };

  return sort.direction === 'asc' ? { field, direction: 'desc' } : null;
};

type MyCollegesTableProps = Readonly<{
  colleges: readonly UserCollege[];
  hasFamilyIncome: boolean;
  onStatusChange: (college: UserCollege, status: UserCollegeStatus) => void;
  onPlanChange: (college: UserCollege, plan: ApplicationPlan) => void;
  onRemove: (college: UserCollege) => void;
}>;

export const MyCollegesTable = (props: MyCollegesTableProps) => (
  <Box sx={scrollSx}>
    <Box sx={groupsSx}>
      {groupByTier(props.colleges).map(({ tier, colleges }) => (
        <TierTable key={tier ?? 'not-rated'} {...props} tier={tier} colleges={colleges} />
      ))}
    </Box>
  </Box>
);

const TierTable = ({
  tier,
  colleges,
  hasFamilyIncome,
  onStatusChange,
  onPlanChange,
  onRemove,
}: MyCollegesTableProps & Readonly<{ tier: CollegeTier | null }>) => {
  const [sort, setSort] = useState<Sort>(null);
  const tableId = `my-colleges-table-${tier ?? 'not-rated'}`;

  const headers = [
    ...COLUMNS.map(({ field, width }) => ({
      content: (
        <Box
          component="button"
          type="button"
          id={`${tableId}-sort-${field}`}
          sx={sortButtonSx(sort?.field === field)}
          onClick={() => setSort(nextSort(sort, field))}
        >
          {STRINGS.columns[field]}
          <AppIcon
            iconName={
              sort?.field !== field
                ? 'ChevronSelectorVertical'
                : sort.direction === 'desc'
                  ? 'ArrowDown'
                  : 'ArrowUp'
            }
            diameter="12px"
            color="currentColor"
          />
        </Box>
      ),
      sx: width ? { width } : undefined,
    })),
    { content: '', sx: { width: '80px' } },
  ];

  return (
    <Box sx={groupSx}>
      <Box sx={groupHeaderSx}>
        <TierChip tier={tier} count={colleges.length} />
      </Box>
      <GenericTable id={tableId} containerSx={tableContainerSx} tableSx={tableSx} headers={headers}>
        {sortColleges(colleges, sort).map((college) => (
          <TableRow key={college.id}>
            <TableCell>
              <CollegeChip id={`my-colleges-college-${college.id}`} college={college} />
            </TableCell>
            <TableCell>
              <StatusChip
                id={`my-colleges-status-${college.id}`}
                college={college}
                onChange={(status) => onStatusChange(college, status)}
              />
            </TableCell>
            <TableCell>
              <PlanPicker
                id={`my-colleges-plan-${college.id}`}
                college={college}
                onChange={(plan) => onPlanChange(college, plan)}
              />
            </TableCell>
            <TableCell>
              <Typography variant="text-sm-medium" sx={noWrapSx}>
                {deadlineText(college)}
              </Typography>
            </TableCell>
            <TableCell>
              <NetCostText college={college} hasFamilyIncome={hasFamilyIncome} />
            </TableCell>
            <TableCell>
              <RequirementTags id={`my-colleges-requirements-${college.id}`} college={college} />
            </TableCell>
            <TableCell align="right">
              <Box sx={rowActionsSx}>
                <IconButton
                  id={`my-colleges-remove-${college.id}`}
                  aria-label={STRINGS.removeAriaLabel(college.name)}
                  onClick={() => onRemove(college)}
                  size="small"
                >
                  <AppIcon iconName="Trash01" diameter="16px" color="Colors/Text/text-tertiary" />
                </IconButton>
                <AwardLetterButton
                  id={`my-colleges-award-letter-${college.id}`}
                  college={college}
                />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </GenericTable>
    </Box>
  );
};

const scrollSx: SxProps<Theme> = {
  overflowX: 'auto',
  minWidth: 0,
};

const groupsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '1040px',
  gap: backrs.spacing['spacing-3xl'],
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: backrs.spacing['spacing-md'],
});

const groupHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const sortButtonSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: isActive ? backrs.colors['Colors/Text/text-brand-secondary'] : 'inherit',
  });

const tableContainerSx: SxProps<Theme> = ({ backrs }) => ({
  overflowX: 'visible',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const tableSx: SxProps<Theme> = ({ backrs }) => ({
  tableLayout: 'fixed',
  '& th': { whiteSpace: 'nowrap', paddingInline: backrs.spacing['spacing-3xl'] },
  '& td': {
    height: '72px',
    paddingBlock: backrs.spacing['spacing-xl'],
    paddingInline: backrs.spacing['spacing-3xl'],
  },
});

const rowActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
});

const noWrapSx: SxProps<Theme> = ({ backrs }) => ({
  whiteSpace: 'nowrap',
  color: backrs.colors['Colors/Text/text-primary'],
});
